import React, {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useCallback,
} from "react";
import "amazon-connect-streams";
import moment from "moment-timezone";
import {
  LayoutDashboard,
  Phone,
  Users,
  Voicemail,
  XCircle,
  Clock,
  MoreHorizontal,
  Briefcase,
} from "lucide-react";

import Dashboard from "../Dashboard/Dashboard";
import Recording from "../Recording/Recording";
import Contacts from "../Contacts/Contacts";
import Voicemails from "../Voicemails/Voicemails";
import MissedCalls from "../MissedCalls/MissedCalls";
import Callbacks from "../Callbacks/Callbacks";
import WhatsApp, {WhatsAppIcon} from "../WhatsApp/WhatsApp";

const ALL_TABS = [
  {name: "Dashboard", icon: LayoutDashboard},
  {name: "Recordings", icon: Phone},
  {name: "Contacts", icon: Users},
  {name: "Voicemails", icon: Voicemail},
  {name: "Missed", icon: XCircle},
  {name: "WhatsApp", icon: WhatsAppIcon},
  {name: "Callbacks", icon: Clock},
  {name: "WeKan", icon: Briefcase},
];

const TabItem = forwardRef(
  (
    {name, Icon, activeTab, setActiveTab, isMeasurement = false, notifications},
    ref,
  ) => {
    const isOverflowActive =
      ALL_TABS.some((t) => t.name === activeTab) &&
      !ALL_TABS.slice(0, 5).some((t) => t.name === activeTab);

    const isActive = activeTab === name;
    const highlightMore = name === "More" && isOverflowActive;

    const baseClasses =
      "flex items-center space-x-2 py-3 px-4 rounded-lg transition-all duration-200 cursor-pointer text-sm font-medium whitespace-nowrap";
    const activeClasses =
      "bg-[#2ba9cd] text-white shadow-lg shadow-[#2ba9cd]/50";
    const inactiveClasses =
      "text-gray-600 hover:bg-gray-100 dark:text-gray-300 hover:bg-gray-200";
    const measurementClasses =
      "absolute left-0 top-0 opacity-0 pointer-events-none";

    // Notification Logic
    let highlight = false;
    if (name === "Missed" && notifications?.missedVoice?.length > 0) {
      highlight = true;
    }
    if (name === "WhatsApp" && notifications?.missedWhatsApp?.length > 0) {
      highlight = true;
    }
    if (name === "Voicemails" && notifications?.voicemail?.length > 0) {
      highlight = true;
    }

    return (
      <div
        ref={ref}
        className={`${baseClasses} ${isMeasurement ? measurementClasses : ""} ${
          isActive || highlightMore ? activeClasses : inactiveClasses
        }`}
        onClick={() => setActiveTab(name)}
      >
        <Icon className="w-5 h-5" />

        {/* Relative wrapper for the label so the dot anchors to the text */}
        <div className="relative inline-block">
          <span>{name}</span>

          {highlight && (
            <span className="absolute -top-1.5 -right-2.5 flex h-2.5 w-2.5">
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600 border border-white"></span>
            </span>
          )}
        </div>
      </div>
    );
  },
);

const CCPPanel = (props) => {
  const [ccpRefreshExhausted, setCcpRefreshExhausted] = useState(false);
  const [ccpInitialized, setCcpInitialized] = useState(false);
  const [agentQueues, setAgentQueues] = useState();
  const [isPriviledged, setIsPriviledged] = useState(false);
  const [queue, setQueue] = useState();
  const [contactAttributes, setContactAttributes] = useState();
  const [auth, setAuth] = useState(false);
  const [outbound, setOutbound] = useState({
    status: false,
    country: undefined,
    phonenumber: undefined,
    queue: undefined,
  });
  const [notifications, setNotifications] = useState({
    missedWhatsApp: [],
    missedVoice: [],
    voicemail: [],
  });

  const [activeTab, setActiveTab] = useState("Dashboard");
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const notificationTimerRef = useRef(null);

  // State to hold the tabs that fit in the current width
  const [visibleTabs, setVisibleTabs] = useState(ALL_TABS);
  // State to hold the tabs that must go into the 'More' dropdown
  const [overflowTabs, setOverflowTabs] = useState([]);

  const navRef = useRef(null); // The main container for the visible tabs
  const tabRefs = useRef({}); // Refs for all individual tab elements (used for width measurement)
  const moreButtonRef = useRef(null); // Ref for the 'More' button itself
  const moreRef = useRef(null); // Container for More button and dropdown

  // Helper function to calculate tab widths from their hidden DOM elements
  const getTabWidths = useCallback(() => {
    const widths = {};
    for (const tab of ALL_TABS) {
      const element = tabRefs.current[tab.name];
      if (element) {
        // Measure element width including padding/margin (offsetWidth)
        // Add a small constant for `space-x-2` margin (8px)
        widths[tab.name] = element.offsetWidth + 8;
      }
    }
    return widths;
  }, []);

  // Main logic to determine which tabs are visible and which overflow
  const recalculateTabs = useCallback(() => {
    // We must have the container and the 'More' button element references to proceed
    if (!navRef.current || !moreButtonRef.current) return;

    const tabWidths = getTabWidths();
    const containerWidth = navRef.current.offsetWidth;
    // Get the width of the More button container (including the space-x-2)
    const moreWidth = moreButtonRef.current.offsetWidth + 8;

    let currentWidth = 0;
    const finalVisibleTabs = [];
    const finalOverflowTabs = [];
    let moreIsNeeded = false;

    // First pass: Check how many tabs fit into the container
    for (const tab of ALL_TABS) {
      const width = tabWidths[tab.name];

      // Check if this tab fits, accounting for the possibility that the 'More' button might be needed.
      // The total width of all visible tabs + the 'More' button (if needed) must be less than containerWidth.
      const widthIfMoreIsNeeded =
        currentWidth + width + (moreIsNeeded ? 0 : moreWidth);
      const widthIfMoreIsNotNeeded = currentWidth + width;

      if (!moreIsNeeded && widthIfMoreIsNotNeeded < containerWidth) {
        // No 'More' button yet, and the tab fits.
        finalVisibleTabs.push(tab);
        currentWidth += width;
      } else if (moreIsNeeded && widthIfMoreIsNeeded < containerWidth) {
        // 'More' is already needed, and this tab fits alongside the 'More' button.
        finalVisibleTabs.push(tab);
        currentWidth += width;
      } else {
        // The tab does not fit. It and all subsequent tabs go into overflow.
        if (finalVisibleTabs.length > 0) {
          // Check if we need to push the last visible tab into overflow to make space for the 'More' button
          // This is the core logic: if the 'More' button is now needed, check if the last visible tab needs to be sacrificed.
          const totalWidthWithMore = currentWidth + moreWidth;

          // If the total width exceeds the container, move the last visible tab to overflow.
          if (totalWidthWithMore > containerWidth) {
            const tabToMove = finalVisibleTabs.pop();
            currentWidth -= tabWidths[tabToMove.name];
            finalOverflowTabs.unshift(tabToMove); // Add to the start of overflow
          }
        }

        finalOverflowTabs.push(tab);
        moreIsNeeded = true;

        // Add remaining tabs to overflow and break the loop
        const currentTabIndex = ALL_TABS.findIndex((t) => t.name === tab.name);
        finalOverflowTabs.push(...ALL_TABS.slice(currentTabIndex + 1));

        // Remove duplicates if any were manually added before the loop break
        const uniqueOverflowTabs = Array.from(
          new Set(finalOverflowTabs.map((t) => t.name)),
        ).map((name) => ALL_TABS.find((t) => t.name === name));

        setOverflowTabs(uniqueOverflowTabs);
        break; // Stop iteration once overflow is determined
      }
    }

    // Only update if changes occurred to prevent unnecessary re-renders
    if (
      visibleTabs.length !== finalVisibleTabs.length ||
      overflowTabs.length !== finalOverflowTabs.length
    ) {
      setVisibleTabs(finalVisibleTabs);
      setOverflowTabs(
        finalOverflowTabs.filter(
          (v, i, a) => a.findIndex((t) => t.name === v.name) === i,
        ),
      ); // Remove duplicates
    }

    if (finalOverflowTabs.length === 0) {
      setVisibleTabs(ALL_TABS);
      setOverflowTabs([]);
    }
  }, [getTabWidths, visibleTabs.length, overflowTabs.length]);

  // Handler for the 'More' tab click
  const handleMoreClick = () => {
    setIsMoreOpen(!isMoreOpen);
  };

  // Handler for clicking an option within 'More'
  const handleMoreOptionClick = (name) => {
    setActiveTab(name);
    setIsMoreOpen(false);
  };

  // Check if the active tab is one of the overflow options
  const isMoreOptionActive = overflowTabs.some(
    (option) => option.name === activeTab,
  );

  const [directOutbound, setDirectOutbound] = useState({
    status: false,
    phonenumber: undefined,
    queue: undefined,
  });

  console.log(
    `[CCP - Current CCP State: ${moment
      .tz(new Date(), process.env.REACT_APP_TIMEZONE)
      .format(
        "YYYY-MM-DD HH:mm:ss",
      )}] =====> [CCP Initialized: ${ccpInitialized},  CCP Refresh Exhausted: ${ccpRefreshExhausted}]`,
  );

  const makeDirectOutboundCallHandler = (phonenumber, queue) => {
    console.log(`Making Direct Outbound Call ======>${phonenumber}, ${queue}`);
    setDirectOutbound({
      status: true,
      phonenumber,
      queue,
    });
  };

  const fetchNotifications = async () => {
    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };
      const resp = await fetch(
        `${process.env.REACT_APP_AWS_ENDPOINT}/notifications`,
        options,
      );
      const data = await resp.json();
      console.log(`Notification Data ======> ${JSON.stringify(data)}`);

      const voicemailNotifications = data.data.filter(
        (x) => x.source_notification?.toLowerCase() === "voicemail",
      );
      const missedWhatsAppNotifications = data.data.filter(
        (x) => x.source_notification?.toLowerCase() === "missedwhatsapp",
      );
      const missedVoiceNotifications = data.data.filter(
        (x) => x.source_notification?.toLowerCase() === "missedvoice",
      );

      setNotifications({
        voicemail: voicemailNotifications,
        missedVoice: missedVoiceNotifications,
        missedWhatsApp: missedWhatsAppNotifications,
      });

      console.log(
        `[CCP - Voicemail Notifications Fetched: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format(
            "YYYY-MM-DD HH:mm:ss",
          )}] =====> ${JSON.stringify(voicemailNotifications)}`,
      );
      console.log(
        `[CCP - Missed WhatsApp Notifications Fetched: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format(
            "YYYY-MM-DD HH:mm:ss",
          )}] =====> ${JSON.stringify(missedWhatsAppNotifications)}`,
      );
      console.log(
        `[CCP - Missed Voice Notifications Fetched: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format(
            "YYYY-MM-DD HH:mm:ss",
          )}] =====> ${JSON.stringify(missedVoiceNotifications)}`,
      );
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Effect for initial calculation and resize observation
  useEffect(() => {
    // Function to run the recalculation after a short delay
    const runRecalculation = () => {
      // Use a timeout to ensure the browser has calculated widths correctly after DOM changes/resizes
      setTimeout(recalculateTabs, 100);
    };

    // Run on initial mount
    runRecalculation();

    // Setup ResizeObserver for responsiveness
    if (navRef.current) {
      const observer = new ResizeObserver(runRecalculation);
      observer.observe(navRef.current);

      // Cleanup
      return () => observer.unobserve(navRef.current);
    }
  }, [recalculateTabs]);

  // Close the dropdown if the user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreRef]);

  useEffect(() => {
    if (props.logoutAgent === true) {
      fetch(`https://${process.env.REACT_APP_CCP_DOMAIN}/logout`, {
        credentials: "include",
        mode: "no-cors",
      }).then(() => {
        // eslint-disable-next-line no-undef
        connect.core.getUpstream().sendUpstream(connect.EventType.TERMINATE);
        window.location.reload();
      });
    }
  }, [props.logoutAgent]);

  useEffect(() => {
    if (
      props.outboundCall?.countryCode &&
      props.outboundCall?.phoneNumber &&
      props.outboundCall?.queueId
    )
      setOutbound({
        status: true,
        country: props.outboundCall.countryCode,
        phonenumber: props.outboundCall.phoneNumber,
        queue: props.outboundCall.queueId,
      });
  }, [props.outboundCall]);

  useEffect(() => {
    console.log(
      `[CCP: ${moment
        .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        .format("YYYY-MM-DD HH:mm:ss")}] =====> Loading CCP`,
    );

    // Custom, louder-mastered ringtones (see custom-ccp/public/sounds).
    // These have to be absolute URLs: the ringtone <audio> element is
    // created inside the CCP iframe (a different origin from this app), so
    // a relative path would resolve against that origin instead of ours.
    const voiceRingtoneUrl = `${window.location.origin}/sounds/voice-ringtone.wav`;
    const chatRingtoneUrl = `${window.location.origin}/sounds/chat-notification.wav`;

    // eslint-disable-next-line no-undef
    connect.agentApp.initApp(
      "ccp",
      "ccp_panel",
      `${process.env.REACT_APP_CCP_URL}/ccp-v2/`,
      {
        ccpParams: {
          loginPopup: true,
          loginPopupAutoClose: true,
          ccpAckTimeout: 10000,
          ccpSynTimeout: 3000,
          ccpLoadTimeout: 10000,
          // `connect.merge` (used internally to combine this with Streams'
          // own softphone defaults) is a SHALLOW merge - it replaces the
          // whole `softphone` object rather than extending it. Streams'
          // default softphone config carries `allowFramedSoftphone: true`,
          // which is what lets the CCP start its actual WebRTC audio engine
          // when it's embedded in an iframe (as it is here). Passing just
          // `{ ringtoneUrl }` here silently dropped that flag: the call
          // session/signaling still worked (agent state, onConnecting,
          // etc.) but no audio engine ever started, so every call rang
          // into total silence and timed out. Both flags must be repeated
          // explicitly any time this object is touched.
          softphone: {
            allowFramedSoftphone: true,
            disableRingtone: false,
            ringtoneUrl: voiceRingtoneUrl,
          },
          // Streams' shorthand `chat.ringtoneUrl` sets the URL but never
          // flips the chat ringtone's `disabled` flag back on (it defaults
          // to disabled) - the explicit `ringtone.chat` override is what
          // actually enables it.
          ringtone: {
            chat: {
              disabled: false,
              ringtoneUrl: chatRingtoneUrl,
            },
          },
        },
        style: "width:100%; height:100%",
      },
    );

    // eslint-disable-next-line no-undef
    connect.core.onInitialized(() => {
      console.log(
        `[CCP - On Initialized: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss")}]=====> Connect Initialized`,
      );
      setCcpRefreshExhausted(false);
      setCcpInitialized(true);
      setAuth(true);
    });

    // eslint-disable-next-line no-undef
    connect.core.onAuthorizeSuccess(() => {
      console.log(
        `[CCP - On Authorize Success: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss")}] =====> Connect Authorized`,
      );
      setAuth(true);
    });

    // eslint-disable-next-line no-undef
    connect.core.onAuthorizeRetriesExhausted(() => {
      console.log(
        `[CCP - OnAuthorizeRetriesExhausted: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format(
            "YYYY-MM-DD HH:mm:ss",
          )}] =====> Connect Authorization Exhaused`,
      );
      setAuth(false);
    });

    // eslint-disable-next-line no-undef
    connect.core.onIframeRetriesExhausted(() => {
      console.log(
        `[CCP - OnIFrameRetriesExhausted: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss")}] =====> Connect CCP Reload Exhausted`,
      );
      setCcpRefreshExhausted(true);
      setCcpInitialized(false);
      setAuth(false);
    });

    // eslint-disable-next-line no-undef
    connect.core.onAuthFail(() => {
      console.log(
        `[CCP - OnAuthFail: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss")}] =====> Connect Authorization Failed`,
      );
      setAuth(false);
    });

    // eslint-disable-next-line no-undef
    connect.core.onAccessDenied(() => {
      console.log(
        `[CCP - OnAccessDenied: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss")}] =====> Connect Access Denied`,
      );
      setAuth(false);
    });

    // eslint-disable-next-line no-undef
    connect.core.onCTIAuthorizeRetriesExhausted(() => {
      console.log(
        `[CCP - onCTIAuthorizeRetriesExhausted: ${moment
          .tz(new Date(), process.env.REACT_APP_TIMEZONE)
          .format(
            "YYYY-MM-DD HH:mm:ss",
          )}] =====> Connect CTI Retries Exhausted!`,
      );
      setAuth(false);
    });

    // eslint-disable-next-line no-undef
    connect.contact((contact) => {
      contact.onPending((contact) => {
        console.log(
          `[CCP - On Pending: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onRefresh((contact) => {
        console.log(
          `[CCP - On Refresh: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onIncoming((contact) => {
        console.log(
          `[CCP - On Incoming: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onAccepted((contact) => {
        console.log(
          `[CCP - On Accepted: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onConnecting((contact) => {
        console.log(
          `[CCP - On Connecting: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
        console.log(
          `[CCP - On Connecting: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
            contact.getQueue(),
          )}`,
        );
        let attributes = contact.getAttributes();

        if (attributes && attributes?.direction?.value === "inbound") {
          let newAttributes = {};
          for (let key in attributes) {
            newAttributes[attributes[key].name] = attributes[key].value;
          }
          console.log(
            `[CCP - On Connecting [Contact Attributes]: ${moment
              .tz(new Date(), process.env.REACT_APP_TIMEZONE)
              .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
              newAttributes,
            )}`,
          );
          setContactAttributes(newAttributes);
        }

        setQueue(contact.getQueue().name);
      });
      contact.onConnected((contact) => {
        console.log(
          `[CCP - On Connected: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onEnded((contact) => {
        // console.log(
        //   `[CCP - On Ended: ${moment
        //     .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        //     .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`
        // );
        setQueue();
        setContactAttributes();
      });
      contact.onMissed((contact) => {
        console.log(
          `[CCP - On Missed: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onDestroy((contact) => {
        // console.log(
        //   `[CCP - On Destroyed: ${moment
        //     .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        //     .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`
        // );
      });
      contact.onACW((contact) => {
        console.log(
          `[CCP - On After Call Work: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
      contact.onError((contact) => {
        console.log(
          `[CCP - On Error: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(contact)}`,
        );
      });
    });

    // eslint-disable-next-line no-undef
    connect.agent((agent) => {
      let agentRoutingProfile = agent.getRoutingProfile();

      let queues = agentRoutingProfile.queues.map((x) => {
        if (x.name && x.name !== null) {
          return {
            name: x.name,
            value: x.queueARN,
          };
        }
      });

      props.agentQueues(queues);
      setAgentQueues(queues);

      const agentPermissions = agent.getPermissions();
      const agentDialableCountries = agent.getConfiguration().dialableCountries;
      const agentSoftphoneConfig = agent.getConfiguration().softphone;
      const agentARN = agent.getConfiguration().agentARN;
      const agentUsername = agent.getConfiguration().username;

      agent.onRefresh((agent) => {
        // console.log(
        //   `[CCP Agent On Refresh: ${moment
        //     .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        //     .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
        //     agent.getName()
        //   )}`
        // );
        console.log(
          `[CCP Agent On Refresh: ${moment
            .tz(new Date(), process.env.REACT_APP_TIMEZONE)
            .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
            agent.getState(),
          )}`,
        );
        // console.log(
        //   `[CCP Agent On Refresh: ${moment
        //     .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        //     .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
        //     agent.getConfiguration()
        //   )}`
        // );
        if (
          agent
            .getConfiguration()
            .permissions.includes("ccpRealtimeContactLens")
        ) {
          setIsPriviledged(true);
        }

        props.agentInfo({
          agentState: {
            ...agent.getState(),
          },
          agentName: agent.getName(),
          agentARN: agentARN,
          agentPermissions: agentPermissions,
          agentDialableCountries: agentDialableCountries,
          agentSoftphoneConfig: agentSoftphoneConfig,
          agentRoutingProfile: agentRoutingProfile,
          agentUsername: agentUsername,
        });

        // console.log(
        //   `[CCP Agent On Refresh: ${moment
        //     .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        //     .format(
        //       "YYYY-MM-DD HH:mm:ss"
        //     )}] =====> Agent State: ${JSON.stringify(agent.getState())}}`
        // );

        if (agent.getState().name === "MissedCallAgent") {
          let offlineState = agent
            .getAgentStates()
            .filter((state) => state.name === "Offline")[0];

          setTimeout(() => {
            console.log(
              `[CCP Agent On Refresh: ${moment
                .tz(new Date(), process.env.REACT_APP_TIMEZONE)
                .format("YYYY-MM-DD HH:mm:ss")}] =====> Function executed!}`,
            );

            agent.setState(
              offlineState,
              {
                success: function () {
                  console.log(
                    `[CCP Agent On Refresh: ${moment
                      .tz(new Date(), process.env.REACT_APP_TIMEZONE)
                      .format(
                        "YYYY-MM-DD HH:mm:ss",
                      )}] =====> State Changed Successfully!}`,
                  );
                },
                failure: function (err) {
                  console.log(
                    `[CCP Agent On Refresh: ${moment
                      .tz(new Date(), process.env.REACT_APP_TIMEZONE)
                      .format(
                        "YYYY-MM-DD HH:mm:ss",
                      )}] =====> Error in State Change ${err.message}!}`,
                  );
                },
              },
              {enqueueNextState: false},
            );
          }, 5000);
        }
      });
      agent.onStateChange((agentStateChange) => {
        // console.log(
        //   `[CCP - Agent On State Changed: ${moment
        //     .tz(new Date(), process.env.REACT_APP_TIMEZONE)
        //     .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
        //     agentStateChange
        //   )}`
        // );
      });
      agent.onRoutable((agent) => {
        //console.log(`[CCP - Agent On Routable: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onNotRoutable((agent) => {
        //console.log(`[CCP - Agent On Not Routable: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onOffline((agent) => {
        //console.log(`[CCP - Agent On Offline: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onError((agent) => {
        //console.log(`[CCP - Agent On Error: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onSoftphoneError((error) => {
        //console.log(`[CCP - Agent On Softphone Error: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(error)}`)
      });
      agent.onWebSocketConnectionLost((agent) => {
        //console.log(`[CCP - Agent On Connection Lost: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onWebSocketConnectionGained((agent) => {
        //console.log(`[CCP - Agent On WebSocket Connection Gained: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onAfterCallWork((agent) => {
        //console.log(`[CCP - Agent On ACW: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(agent)}`)
      });
      agent.onMuteToggle((obj) => {
        //console.log(`[CCP - Agent On Mute Toggle: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(obj)}`)
      });
      agent.onSpeakerDeviceChanged((obj) => {
        //console.log(`[CCP - Agent On Speaker Device Changed: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(obj)}`)
      });
      agent.onMicrophoneDeviceChanged((obj) => {
        //console.log(`[CCP - Agent On Microphone Device Changed: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(obj)}`)
      });
      agent.onRingerDeviceChanged((obj) => {
        //console.log(`[CCP - Agent On Ringer Changed: ${moment.tz(new Date(), process.env.REACT_APP_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')}] =====> ${JSON.stringify(obj)}`)
      });
    });
    // Runs once on mount. This registers connect.contact()/connect.agent()
    // (and every connect.core.onX handler) exactly one time - Streams treats
    // those as long-lived subscriptions, not something to re-register.
    // Previously this effect depended on [outbound, directOutbound], so
    // placing a single outbound call re-ran the whole thing: initApp fired
    // again, and connect.contact()/connect.agent() added a brand-new,
    // never-cleaned-up set of listeners on top of the existing ones. Every
    // outbound call attempt left the CCP with more duplicate listeners than
    // the last, which is exactly the kind of thing that makes outbound
    // calls connect inconsistently over a session. Outbound-call placement
    // now lives in its own effect below, keyed on [outbound, directOutbound].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Places an outbound call whenever `outbound` (queue-transfer initiated
  // call) or `directOutbound` (click-to-call) flips to status: true. Kept
  // separate from the CCP-init effect above so placing a call never
  // re-triggers connect.agentApp.initApp(...) or re-subscribes the
  // connect.contact()/connect.agent() handlers.
  useEffect(() => {
    const processOutboundCall = (endpoint, queueARN) => {
      console.log(
        `Processing Outbound Call ======> ${JSON.stringify(
          endpoint,
        )}, ${queueARN}`,
      );
      // eslint-disable-next-line no-undef
      const agent = new connect.Agent();
      agent.connect(endpoint, {
        queueARN,
        success: function () {
          console.log(
            `[CCP - Outbound Call: ${moment
              .tz(new Date(), process.env.REACT_APP_TIMEZONE)
              .format("YYYY-MM-DD HH:mm:ss")}] =====> Outbound Call Connected!`,
          );
          setOutbound({
            status: false,
            country: undefined,
            phonenumber: undefined,
            queue: undefined,
          });
          setDirectOutbound({
            status: false,
            phonenumber: undefined,
            queue: undefined,
          });
        },
        failure: function (err) {
          console.error(
            `[CCP - Outbound Call: ${moment
              .tz(new Date(), process.env.REACT_APP_TIMEZONE)
              .format(
                "YYYY-MM-DD HH:mm:ss",
              )}] =====> Outbound Call Connection Failed [${err.message}]!`,
          );
          setOutbound({
            status: false,
            country: undefined,
            phonenumber: undefined,
            queue: undefined,
          });
          setDirectOutbound({
            status: false,
            phonenumber: undefined,
            queue: undefined,
          });
        },
      });
    };

    if (outbound && outbound?.status === true) {
      // eslint-disable-next-line no-undef
      const endpoint = connect.Endpoint.byPhoneNumber(
        `${outbound.country}${outbound.phonenumber}`,
      );
      const queueARN = outbound.queue;

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };

      fetch(
        `${
          process.env.REACT_APP_AWS_ENDPOINT
        }/get-customer-info/phone/${outbound.country.replace("+", "")}${
          outbound.phonenumber
        }`,
        options,
      )
        .then((resp) => resp.json())
        .then((data) => {
          let contactAttributes = {
            customer_phonenumber: `${outbound.country}${outbound.phonenumber}`,
          };

          if (data) {
            contactAttributes = {
              ...data,
              customer_phonenumber: `${outbound.country}${outbound.phonenumber}`,
            };
          }
          console.log(
            `[CCP - On Outbound Call: ${moment
              .tz(new Date(), process.env.REACT_APP_TIMEZONE)
              .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
              contactAttributes,
            )}`,
          );
          setContactAttributes(contactAttributes);
          processOutboundCall(endpoint, queueARN);
        })
        .catch((error) => {
          console.log(`Error in getting customer =======> ${error}`);
          processOutboundCall(endpoint, queueARN);
        });
    }

    if (directOutbound && directOutbound?.status === true) {
      console.log(`Connecting to =======> ${directOutbound.phonenumber}`);
      // eslint-disable-next-line no-undef
      const endpoint = connect.Endpoint.byPhoneNumber(
        `${directOutbound.phonenumber}`,
      );
      const queueARN = directOutbound.queue;

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };

      fetch(
        `${
          process.env.REACT_APP_AWS_ENDPOINT
        }/get-customer-info/phone/${directOutbound.phonenumber?.replace(
          "+",
          "",
        )}`,
        options,
      )
        .then((resp) => resp.json())
        .then((data) => {
          let contactAttributes = {
            customer_phonenumber: `${directOutbound.phonenumber}`,
          };

          if (data) {
            contactAttributes = {
              ...data,
              customer_phonenumber: `${directOutbound.phonenumber}`,
            };
          }
          console.log(
            `[CCP - On Outbound Call: ${moment
              .tz(new Date(), process.env.REACT_APP_TIMEZONE)
              .format("YYYY-MM-DD HH:mm:ss")}] =====> ${JSON.stringify(
              contactAttributes,
            )}`,
          );
          setContactAttributes(contactAttributes);
          processOutboundCall(endpoint, queueARN);
        })
        .catch((error) => {
          console.log(`Error in getting customer =======> ${error}`);
          processOutboundCall(endpoint, queueARN);
        });
    }
  }, [outbound, directOutbound]);

  useEffect(() => {
    if (notificationTimerRef.current) {
      clearInterval(notificationTimerRef.current);
    }

    // Fetch immediately on mount instead of waiting for the first 30s
    // interval tick - otherwise every tab shows no notification dots at
    // all (even genuinely unread ones) for up to 30 seconds after the CCP
    // loads, and a badge just cleared via PATCH won't reflect until the
    // next tick either.
    fetchNotifications();

    notificationTimerRef.current = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => {
      if (notificationTimerRef.current) {
        clearInterval(notificationTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="flex w-full space-x-2 p-4">
      <div
        className="w-[30%] justify-center items-center bg-white p-4 shadow-2xl rounded-xl border border-gray-200"
        id="ccp_panel"
        style={{display: ccpInitialized === true ? "block" : "none"}}
      ></div>

      {!ccpInitialized && !ccpRefreshExhausted && (
        <div className="flex flex-col w-full items-center justify-center">
          <img
            className="w-10"
            src="./images/three-dots.svg"
            alt="Loading..."
          />
          <p className="text-lg text-gray-600">Please login!</p>
          <p className="text-sm text-gray-400">
            Kindly refresh your browser, if CCP is not shown even after login!
          </p>
        </div>
      )}

      {!ccpInitialized && ccpRefreshExhausted && (
        <div className="flex flex-col w-full items-center justify-center">
          <p className="text-lg text-gray-600">Kindly refresh your browser!</p>
          <p className="text-sm text-gray-400">
            Max re-tries to initialize CCP is exhausted.
          </p>
        </div>
      )}

      {ccpInitialized && (
        <div className="flex flex-col w-[70%] bg-gray-50 rounded-xl shadow-lg overflow-hidden">
          {/*
          Hidden tabs for width measurement. They must be rendered for refs to work,
          but absolutely positioned to not affect the layout.
        */}
          <div className="absolute top-0 left-0 -z-10 flex p-4">
            {ALL_TABS.map((tab) => (
              <TabItem
                key={`measure-${tab.name}`}
                ref={(el) => (tabRefs.current[tab.name] = el)}
                name={tab.name}
                Icon={tab.icon}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isMeasurement={true} // Mark as invisible measurement element
                notifications={notifications}
              />
            ))}
            {/* Measurement element for the More button */}
            <button
              ref={moreButtonRef}
              className="flex items-center space-x-2 py-3 px-4 rounded-lg text-sm font-medium whitespace-nowrap absolute opacity-0 pointer-events-none"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span>More</span>
            </button>
          </div>

          {/* Tab Navigation Area - NO SCROLL. Removed overflow-hidden to fix dropdown clipping. */}
          <nav
            ref={navRef}
            className="flex flex-nowrap bg-white border-b border-gray-200 pb-2 space-x-2 p-4"
          >
            {/* Render Visible Tabs */}
            {visibleTabs.map((tab) => (
              <TabItem
                key={tab.name}
                name={tab.name}
                Icon={tab.icon}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                notifications={notifications}
              />
            ))}

            {overflowTabs.length > 0 && (
              <div className="relative ml-auto" ref={moreRef}>
                <button
                  className={`flex items-center space-x-2 py-3 px-4 rounded-lg transition-all duration-200 cursor-pointer text-sm font-medium whitespace-nowrap
                  ${
                    isMoreOpen || isMoreOptionActive
                      ? "bg-[#2ba9cd] text-white shadow-lg shadow-[#2ba9cd]/50"
                      : "text-gray-600 hover:bg-gray-100 hover:bg-gray-200"
                  }`}
                  onClick={handleMoreClick}
                >
                  <MoreHorizontal className="w-5 h-5" />
                  <span>More</span>
                </button>

                {/* Dropdown Content */}
                {isMoreOpen && (
                  <div className="absolute right-0 mt-2 w-40 rounded-xl shadow-2xl z-10 origin-top-right transform scale-100 transition duration-150 ease-out animate-fade-in-up border border-gray-100">
                    <div className="py-1">
                      {overflowTabs.map((option) => (
                        <div
                          key={option.name}
                          onClick={() => handleMoreOptionClick(option.name)}
                          className={`flex items-center space-x-3 px-4 py-2 text-sm cursor-pointer transition-colors duration-150
                          ${
                            activeTab === option.name
                              ? "bg-[#2ba9cd] text-white hover:bg-[#0b476c]"
                              : "bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          <option.icon className="w-4 h-4" />
                          <span>{option.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Tab Content Area */}
          <div className="overflow-hidden h-[80vh]">
            {activeTab === "Dashboard" && (
              <Dashboard
                queue={queue}
                contactAttributes={contactAttributes}
                agentQueues={agentQueues}
                auth={auth}
              />
            )}
            {activeTab === "Recordings" && (
              <Recording
                isPriviledged={isPriviledged}
                agentQueues={agentQueues}
                auth={auth}
              />
            )}
            {activeTab === "Contacts" && (
              <Contacts
                isPriviledged={isPriviledged}
                agentQueues={agentQueues}
                makeDirectOutboundCall={makeDirectOutboundCallHandler}
                auth={auth}
              />
            )}
            {activeTab === "Voicemails" && (
              <Voicemails
                isPriviledged={isPriviledged}
                agentQueues={agentQueues}
                auth={auth}
                notifications={notifications.voicemail}
                onNotificationRead={fetchNotifications}
              />
            )}
            {activeTab === "Missed" && (
              <MissedCalls
                isPriviledged={isPriviledged}
                agentQueues={agentQueues}
                makeDirectOutboundCall={makeDirectOutboundCallHandler}
                auth={auth}
                notifications={notifications}
                onNotificationRead={fetchNotifications}
              />
            )}
            {activeTab === "WhatsApp" && (
              <WhatsApp
                agentQueues={agentQueues}
                makeDirectOutboundCall={makeDirectOutboundCallHandler}
                notifications={notifications}
                onNotificationRead={fetchNotifications}
              />
            )}
            {activeTab === "Callbacks" && (
              <Callbacks
                isPriviledged={isPriviledged}
                agentQueues={agentQueues}
                makeDirectOutboundCall={makeDirectOutboundCallHandler}
                auth={auth}
              />
            )}
            {activeTab === "WeKan" && (
              <div
                className="bg-gray-50 p-2 border border-gray-200 rounded-lg overflow-hidden"
                style={{height: "80vh", overflow: "hidden"}}
              >
                <iframe
                  src="https://wekan.propharmaresearch.net/b/4Cdyo5cBBsAQb4XjY/lipidil"
                  title="FormStack External Link"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Removed unused custom-scrollbar styles */}
      {/* Animation Styles */}
      <style>{`
        /* Simple animation for dropdown */
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInDown 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default React.memo(CCPPanel);
